import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlignLeft,
  AtSign,
  Calendar,
  CheckSquare,
  ChevronDown,
  FileUp,
  GripVertical,
  Hash,
  Heading2,
  Type,
  User2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { AddFieldButton } from "./add-field-button";
import { FieldPreview } from "./field-preview";
import type { FormField, FormFieldKind } from "@colab/shared";

// Single sortable field row inside a section. Drag handle, kind icon, label,
// machine-name, required badge, inline preview. Clicking anywhere except
// the handle and the preview selects it for editing in the right panel.

const FIELD_KIND_LABEL: Record<FormFieldKind, string> = {
  text: "Short text",
  long_text: "Long text",
  email: "Email",
  number: "Number",
  date: "Date",
  partner_lookup: "Partner lookup",
  section_heading: "Section heading",
  file: "File upload",
  select: "Select",
  checkbox: "Checkbox",
};

export function FieldBlock({
  field,
  selected,
  onSelect,
  onAddBelow,
}: {
  field: FormField;
  selected: boolean;
  onSelect: () => void;
  onAddBelow: (kind: FormFieldKind) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div className="group relative">
      <div
        ref={setNodeRef}
        style={style}
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect();
          }
        }}
        className={cn(
          "flex items-start gap-2 rounded-md border bg-background px-3 py-2.5 transition",
          selected
            ? "border-accent ring-1 ring-accent/30"
            : "border-border/70 hover:border-foreground/20",
          isDragging && "opacity-40",
        )}
      >
        <button
          type="button"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          className="mt-1 cursor-grab text-muted-foreground/60 hover:text-foreground active:cursor-grabbing"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>

        <FieldKindIcon kind={field.kind} />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">
              {field.label || <em className="text-muted-foreground">Untitled field</em>}
            </span>
            {field.required && (
              <Badge variant="outline" className="h-4 px-1 text-[9px] uppercase tracking-wider">
                Required
              </Badge>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
            <span className="font-mono">{field.name}</span>
            <span>·</span>
            <span>{FIELD_KIND_LABEL[field.kind]}</span>
          </div>

          <div className="mt-2">
            <FieldPreview field={field} />
          </div>
        </div>
      </div>

      {/* Hover-only "+" between fields. Anchored to the bottom edge so it
          appears between this row and the next. */}
      <div className="pointer-events-none absolute bottom-0 left-1/2 z-10 -translate-x-1/2 translate-y-1/2 opacity-0 transition group-hover:pointer-events-auto group-hover:opacity-100">
        <AddFieldButton
          onAdd={onAddBelow}
          variant="inline"
          ariaLabel="Add field below"
        />
      </div>
    </div>
  );
}

function FieldKindIcon({ kind }: { kind: FormFieldKind }) {
  const Icon =
    kind === "text"
      ? Type
      : kind === "long_text"
        ? AlignLeft
        : kind === "email"
          ? AtSign
          : kind === "number"
            ? Hash
            : kind === "date"
              ? Calendar
              : kind === "partner_lookup"
                ? User2
                : kind === "section_heading"
                  ? Heading2
                  : kind === "file"
                    ? FileUp
                    : kind === "select"
                      ? ChevronDown
                      : CheckSquare;
  return <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />;
}
